import type { Editor } from "@tiptap/core"
import { AllSelection, TextSelection } from "@tiptap/pm/state"
import { OPERATION_CONTRACTS } from "../analytics/operations"
import type { CardId } from "../analytics/types"
import { createTitledCard, getCardId, nextCardColor } from "./card"

/**
 * Keyboard shortcut that handles enter in a lessonTitle.
 *
 * - Deletes selection
 * - Moves non-deleted title text after the cursor into the next card
 * - Sets cursor to the start of the next card
 */
export function splitLessonTitle(editor: Editor, nodeName: string): boolean {
  const { state } = editor
  const { selection, schema } = state

  if (selection instanceof AllSelection && nodeName === "lessonTitle") {
    // Delete everything, then put the cursor in the node below the title.
    editor.commands.command(({ tr }) => {
      tr.deleteSelection()
      tr.setSelection(TextSelection.near(tr.doc.resolve(4)))
      return true
    })

    return true
  }

  if (selection.$from.parent.type.name === nodeName) {
    // Whether the split needs a new card is only decidable after the selection delete below, so
    // the we set this in the first command and run the label command second.
    let newCardId: CardId | null = null

    const chain = editor.chain()
    chain.command(({ tr }) => {
      // Delete the selection.
      tr.deleteSelection()

      // Now the selection is a cursor within the title. Handle enter for this case.

      // Find the text after the cursor within the title, if any.
      const textToMoveStart = tr.selection.to
      const textToMoveEnd = tr.selection.$to.posAtIndex(0) + tr.selection.$to.parent.content.size
      const textToMove = tr.doc.textBetween(textToMoveStart, textToMoveEnd)

      // Delete textToMove.
      if (textToMove) tr.delete(textToMoveStart, textToMoveEnd)

      // If there is no node below the title (at the same depth) that can hold text
      // (e.g. none, or an atomic cardGoogleClassroom), add a card, including
      // textToMove if present.
      const $from = tr.doc.resolve(selection.from)
      const titleParent = $from.node(-1)
      if (titleParent.childCount < 2 || titleParent.child(1).type.name === "cardGoogleClassroom") {
        const newCard = createTitledCard(schema, nextCardColor(tr.doc), textToMove)
        newCardId = getCardId(newCard)
        tr.insert($from.posAtIndex(1, -1), newCard)
      } else {
        // Create a new paragraph in the node below the title, adding textToMove if present.
        const afterTitleNode = $from.node(-1).child(1)
        const afterTitleNodePos = $from.posAtIndex(1, -1)
        // The pos of afterTitleNode's non-title content within afterTitleNode.
        const contentDelta =
          afterTitleNode.type.name === "titledCard" ? 1 + afterTitleNode.firstChild!.nodeSize : 1
        tr.insert(
          afterTitleNodePos + contentDelta,
          schema.node("paragraph", null, textToMove ? [schema.text(textToMove)] : undefined),
        )
      }

      // Move the cursor to the node below the title, at the start of the first non-title block.
      {
        const $from = tr.doc.resolve(selection.from)
        const afterTitleNode = $from.node(-1).child(1)
        const afterTitleNodePos = $from.posAtIndex(1, -1)
        // The pos of afterTitleNode's non-title content within afterTitleNode.
        const contentDelta =
          afterTitleNode.type.name === "titledCard" ? 1 + afterTitleNode.firstChild!.nodeSize : 1
        tr.setSelection(TextSelection.create(tr.doc, afterTitleNodePos + contentDelta + 1))
        tr.scrollIntoView()
      }

      return true
    })
    chain.label(OPERATION_CONTRACTS.OP_SPLIT_LESSON_TITLE, "keyboard", { newCardId })
    chain.run()

    return true
  }

  return false
}
